import type { AuthorizationDTO } from 'src/types/dto/authorization.dto';
import type { AppointmentDTO } from 'src/types/dto/appointment.dto';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import type { ActivityDTO, InspectorDTO } from 'src/types/dto';

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@mui/material';
import { gridClasses } from '@mui/x-data-grid';
import { AppDataGrid } from 'src/components/app-data-grid';


import { UserIcon } from 'src/components/user-icon';
import { TagsList } from 'src/components/tags-list';
import { Iconify } from 'src/components/iconify';
import { Scheduled } from 'src/sections/visit/components/scheduled';
import { getAuthorizationName } from 'src/utils/competency';

type TeamTableData = {
  id: number;
  resource: InspectorDTO;
  lead: boolean;
  competencies: AuthorizationDTO[] | undefined;
  appointments: AppointmentDTO[];
};

export function ActivityTeamTable({ activity }: { activity: ActivityDTO }) {
  const { t, i18n } = useTranslation(['activities', 'organization']);
  const [tableData, setTableData] = useState<TeamTableData[]>([]);

  useEffect(() => {
    if (!activity.appointments) {
      setTableData([]);
      return;
    }

    const inspectorMap = new Map<number, {
      inspector: InspectorDTO;
      appointments: AppointmentDTO[];
      competencies: AuthorizationDTO[];
    }>();

    activity.appointments.forEach((appointment) => {
      const inspectorId = appointment.inspector.id;

      if (!inspectorMap.has(inspectorId)) {
        inspectorMap.set(inspectorId, {
          inspector: appointment.inspector,
          appointments: [],
          competencies: appointment.inspector.authorizations || [],
        });
      }

      inspectorMap.get(inspectorId)!.appointments.push(appointment);
    });

    const data = Array.from(inspectorMap.values()).map((inspectorData) => ({
      id: inspectorData.inspector.id,
      resource: inspectorData.inspector,
      competencies: inspectorData.competencies,
      appointments: inspectorData.appointments,
      lead: activity.leadAuditor?.id === inspectorData.inspector.id,
    }));

    setTableData(data);
  }, [activity]);

  const columns: GridColDef[] = [
    {
      field: 'resource',
      headerName: t('activityModal.details.sections.team.inspector'),
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => (
        <UserIcon name={params.row.resource.name} type={params.row.resource.type} />
      ),
      type: 'string',
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'lead',
      headerName: t('activityModal.details.sections.team.lead'),
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) =>
        params.row.lead ? <Iconify icon="ic:round-check-circle" color="success.main" /> : null,
      type: 'string',
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'competencies',
      headerName: t('activityModal.details.sections.team.competencies'),
      flex: 3,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => {
        const requiredCompetencies = params.row.competencies?.filter((e: AuthorizationDTO) =>
          e.competency && activity.activityCompetencies?.some((c) => c.competency?.id === e.competency.id)
        ) ?? [];
        return (
          <Box id={`competencies-container-${params.row.id}`} sx={{ maxHeight: 24, width: '90%' }}>
            <TagsList
              containerIdentifier={`competencies-container-${params.row.id}`}
              tags={requiredCompetencies.map((e: AuthorizationDTO) => ({
                id: e.competency!.id,
                label: getAuthorizationName(e, i18n),
                color: 'info',
                variant: 'filled',
              }))}
              color="info"
              variant="filled"
              showTooltip
            />
          </Box>
        );
      },
      type: 'string',
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'appointments',
      headerName: t('activityModal.details.sections.team.scheduled'),
      flex: 1,
      sortable: false,
      filterable: false,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Scheduled
          appointments={params.row.appointments}
          status={activity.status}
          timezoneId={activity.site?.timezoneId}
        />
      ),
      type: 'string',
      align: 'left',
      headerAlign: 'left',
    },
  ];

  const totalRows = tableData.length;

  return (
    <AppDataGrid 
      columns={columns} 
      rows={tableData}
      columnHeaderHeight={32}
      rowHeight={40}
      disableRowSelectionOnClick
      disableColumnFilter
      disableColumnMenu
      pageSizeOptions={[50]}
      initialState={{
        pagination: { paginationModel: { pageSize: 50 } },
      }}
      hideFooterPagination={totalRows <= 50}
      hideFooter={totalRows <= 50}
      sx={{
        [`& .${gridClasses.cell}`]: {
          alignItems: 'center',
          display: 'inline-flex',
          padding: '8px',
          borderBottomStyle: 'solid',
        },
        [`& .${gridClasses.columnHeader}`]: {
          borderBottomStyle: 'solid',
        },
        '&.MuiDataGrid-root': { height: 'auto' },
        '.MuiDataGrid-columnSeparator': {
          display: 'none',
        },
      }}
    />
  );
}
